import { useEffect, useState } from "react";
import { Card, Tab, Tabs } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import AddStartupAndInnovation from "./AddStartupAndInnovation";
import AddMembers from "./AddMembers";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import HelperService from "../../utility/HelperService";

const StartupAndInnovation = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);

  const [data, setData] = useState<any>(null);
  const [isLoading, setLoading] = useState(false);

  const [editingMember, setEditingMember] = useState<any>(null);
  const [mode, setMode] = useState<"add" | "edit">("add");

  const [showMemberDeleteModal, setShowMemberDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<string | null>(null);

  const [draggedMember, setDraggedMember] = useState<any>(null);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    setLoading(true);
    WebService.getAPI({
      action: "startup-and-innovation/getContent",
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};
        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        setData(res.data.data);
      })

      .finally(() => setLoading(false));
  };

  // ---------- GROUP MEMBERS TYPE-WISE ----------
  const groupedMembers =
    data?.members?.reduce((acc: any, m: any) => {
      if (!acc[m.type]) acc[m.type] = [];
      acc[m.type].push(m);
      return acc;
    }, {}) || {};

  // ---------- DELETE ----------
  const handleDeleteMember = (memberId: string) => {
    const updated = data.members.filter((m: any) => m._id !== memberId);

    const body = new FormData();
    body.append("members", JSON.stringify(updated));
    body.append("id", data._id);

    WebService.fileUploadAPI({
      action: "startup-and-innovation/addMembers",
      body,
    }).then(() => {
      toast.success("Member deleted");
      getData();
    });
  };

  // ---------- REORDER ----------
  const handleMemberDrop = (type: string, index: number) => {
    if (!draggedMember || draggedMember.type !== type) return;

    const sameType = data.members.filter((m: any) => m.type === type);
    const [moved] = sameType.splice(draggedMember.index, 1);
    sameType.splice(index, 0, moved);

    let cursor = 0;
    const merged = data.members.map((m: any) =>
      m.type === type ? sameType[cursor++] : m,
    );

    const body = new FormData();
    body.append("members", JSON.stringify(merged));
    body.append("id", data._id);

    WebService.fileUploadAPI({
      action: "startup-and-innovation/addMembers",
      body,
    }).then(() => {
      toast.success("Order updated");
      getData();
    });

    setDraggedMember(null);
  };

  return (
    <>
      {/* MAIN INFO MODAL */}
      <AddStartupAndInnovation
        show={showAddModal}
        handleClose={() => {
          setShowAddModal(false);
          getData();
        }}
        data={data}
      />

      {/* MEMBERS MODAL */}
      <AddMembers
        show={showMembersModal}
        handleClose={() => {
          setEditingMember(null);
          setShowMembersModal(false);
          getData();
        }}
        data={data}
        member={editingMember}
        mode={mode}
      />

      <div className="events-main">
        <Card className="event-card">
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-center">
            <div className="page-title">Startup & Innovation</div>
          </div>

          {/* MAIN TABS */}
          <Tabs defaultActiveKey="info" className="mt-3">
            {/* ================= INFO TAB ================= */}
            <Tab eventKey="info" title="Info">
              <div className="p-3">
                {isLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: 120 }}
                  >
                    <span className="spinner-border" />
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-end mb-3">
                      {(data ? pagePermission.canEdit : pagePermission.canAdd) && (
                        <CustomButton
                          isLoading={false}
                          label={data ? "Edit" : "Add"}
                          onClick={() => setShowAddModal(true)}
                          style={{ width: 120 }}
                        />
                      )}
                    </div>

                    {/* LOGO */}
                    {data?.logo && (
                      <img
                        src={data.logo}
                        alt="Logo"
                        className="border rounded p-2 mb-3"
                        style={{ width: 300, display: "block" }}
                      />
                    )}

                    {data?.title && (
                      <>
                        <strong>Title</strong>
                        <div className="mb-2">{data?.title}</div>
                      </>
                    )}

                    {data?.description && (
                      <>
                        <strong>Description</strong>
                        <div className="ql-snow">
                        <div
                          className="mb-3 px-0 ql-editor"
                          dangerouslySetInnerHTML={{ __html: HelperService.getPreviewableHTML(data.description) }}
                        />
                        </div>
                      </>
                    )}

                    {/* ECO SYSTEM */}
                    {data?.ecoSystem?.length > 0 && (
                      <>
                        <strong>Eco System</strong>
                        <div className="d-flex flex-wrap gap-3 mt-2">
                          {data?.ecoSystem.map((e: any, i: number) => (
                            <div
                              key={i}
                              className="border rounded p-3"
                              style={{ width: 240 }}
                            >
                              {e.image && (
                                <img
                                  src={e.image}
                                  alt={e.title}
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    objectFit: "contain",
                                  }}
                                />
                              )}
                              <div className="fw-semibold mt-2">{e.title}</div>
                              <div className="text-muted small">
                                {e.description}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* ================= KEY ECOSYSTEM ENABLERS ================= */}
                    {Array.isArray(data?.keyEcoSystemEnablers) &&
                      data.keyEcoSystemEnablers.length > 0 && (
                        <>
                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: 600,
                              marginTop: 32,
                              marginBottom: 16,
                            }}
                          >
                            Key Ecosystem Enablers
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(220px, 1fr))",
                              gap: 16,
                            }}
                          >
                            {data.keyEcoSystemEnablers.map(
                              (item: any, index: number) => (
                                <div
                                  key={item._id || index}
                                  style={{
                                    background: "#f9feff",
                                    border: "1px solid #d9f0f6",
                                    borderRadius: 10,
                                    padding: "14px 16px",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#0f172a",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                    cursor: "default",
                                  }}
                                >
                                  {item.title}
                                </div>
                              ),
                            )}
                          </div>
                        </>
                      )}
                    {/* ================= FUND RAISING SUPPORT ================= */}
                    {Array.isArray(data?.fundRaisingSupport) &&
                      data.fundRaisingSupport.length > 0 && (
                        <>
                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: 600,
                              marginTop: 40,
                              marginBottom: 16,
                            }}
                          >
                            Fund Raising Support for Start-ups
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 16,
                            }}
                          >
                            {data.fundRaisingSupport.map(
                              (item: any, index: number) => (
                                <div
                                  key={item._id || index}
                                  style={{
                                    width: 232,
                                    height: 106,
                                    border: "1.5px solid #4da3ff",
                                    borderRadius: 8,
                                    background: "#ffffff",
                                    padding: "12px 14px",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: "#1f2937",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                    lineHeight: 1.4,
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                  }}
                                >
                                  {item.title}
                                </div>
                              ),
                            )}
                          </div>
                        </>
                      )}
                    {/* REQUIREMENTS */}
                    {data?.requirements?.length > 0 && (
                      <>
                        <strong className="d-block mt-4">Requirements</strong>

                        <div style={{ marginTop: 12 }}>
                          {data.requirements.map((r: any, i: number) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                marginBottom: 8,
                              }}
                            >
                              {/* DOT */}
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: "#0d0d0eff",
                                  marginTop: 6,
                                  marginRight: 10,
                                  flexShrink: 0,
                                }}
                              />

                              {/* TEXT */}
                              <span style={{ fontSize: 14, color: "#1f2937" }}>
                                {r.title}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </Tab>

            {/* ================= MEMBERS TAB ================= */}
            <Tab eventKey="members" title="Members">
              <div className="p-3">
                <div className="d-flex justify-content-end mb-3">
                  {pagePermission.canEdit && (
                    <CustomButton
                      isLoading={false}
                      label="Add Member"
                      onClick={() => {
                        setMode("add");
                        setEditingMember(null);
                        setShowMembersModal(true);
                      }}
                      style={{ width: 180 }}
                    />
                  )}
                </div>

                {Object.keys(groupedMembers).length === 0 ? (
                  <div>No members found</div>
                ) : (
                  <Tabs
                    defaultActiveKey={Object.keys(groupedMembers)[0]}
                    className="mt-2"
                  >
                    {Object.keys(groupedMembers).map((type) => (
                      <Tab
                        key={type}
                        eventKey={type}
                        title={type.replace("_", " ")}
                      >
                        <div className="d-flex flex-wrap gap-3 mt-3">
                          {groupedMembers[type].map(
                            (member: any, index: number) => (
                              <div
                                key={member._id}
                                draggable
                                onDragStart={() =>
                                  setDraggedMember({ type, index })
                                }
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => handleMemberDrop(type, index)}
                                className="border rounded p-3 d-flex flex-column align-items-center"
                                style={{
                                  width: 180,
                                  cursor: "move",
                                  background: "#fff",
                                }}
                              >
                                <span
                                  title="Drag"
                                  style={{
                                    cursor: "grab",
                                    color: "#888",
                                    marginBottom: 6,
                                  }}
                                >
                                  ☰
                                </span>

                                <img
                                  src={member.logo}
                                  alt={member.name}
                                  style={{
                                    width: 100,
                                    height: 100,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                />

                                <strong className="mt-2">{member.name}</strong>

                                <div className="d-flex gap-3 mt-2">
                                  {pagePermission.canEdit && <PencilFill
                                    className="cursor-pointer"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingMember(member);
                                      setShowMembersModal(true);
                                    }}
                                  />}
                                  {pagePermission.canDelete && <TrashFill
                                    color="#DC143C"
                                    className="cursor-pointer"
                                    onClick={() => {
                                      setMemberToDelete(member._id);
                                      setShowMemberDeleteModal(true);
                                    }}
                                  />}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </Tab>
                    ))}
                  </Tabs>
                )}
              </div>
            </Tab>
          </Tabs>
        </Card>

        {showMemberDeleteModal && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowMemberDeleteModal(false);
                      setMemberToDelete(null);
                    }}
                  />
                </div>

                <div className="modal-body">
                  Are you sure you want to delete this member?
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowMemberDeleteModal(false);
                      setMemberToDelete(null);
                    }}
                  >
                    No
                  </button>

                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      if (!memberToDelete) return;

                      handleDeleteMember(memberToDelete);

                      setShowMemberDeleteModal(false);
                      setMemberToDelete(null);
                    }}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StartupAndInnovation;
